import { valueExists } from '../utils';

const parseDateValue = (value) => {
	if (!value) {
		return null;
	}

	const parsedDate = new Date(value);
	return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const getOrderedKnownExpiryValues = (expiryDates = {}) => {
	return Object.keys(expiryDates || {})
		.map(selection => Number.parseInt(selection, 10))
		.filter(selection => Number.isInteger(selection) && selection > 0)
		.sort((left, right) => left - right)
		.map(selection => expiryDates?.[selection])
		.filter(Boolean);
};

const resolveEarliestExpiryValue = (expiryDateCollections = []) => {
	let earliestDate = null;
	let earliestValue = null;

	expiryDateCollections.forEach((expiryDates) => {
		getOrderedKnownExpiryValues(expiryDates).forEach((value) => {
			const parsedDate = parseDateValue(value);
			if (!parsedDate) {
				return;
			}

			if (!earliestDate || parsedDate.getTime() < earliestDate.getTime()) {
				earliestDate = parsedDate;
				earliestValue = value;
			}
		});
	});

	return earliestValue;
};

const mergeLegacyPlanogramAndPickedExpiryDates = ({
	planogramExpiryDates,
	pickedExpiryDates,
	quantity,
}) => {
	const mergedExpiryDates = {};
	const usedPickedSelections = new Set();

	for (let selection = 1; selection <= quantity; selection += 1) {
		const existingPlanogramDate = planogramExpiryDates?.[selection];
		if (existingPlanogramDate) {
			mergedExpiryDates[selection] = existingPlanogramDate;
			continue;
		}

		const pickedDateForSameSelection = pickedExpiryDates?.[selection];
		if (pickedDateForSameSelection) {
			mergedExpiryDates[selection] = pickedDateForSameSelection;
			usedPickedSelections.add(selection);
		}
	}

	const remainingPickedValues = Object.keys(pickedExpiryDates || {})
		.map(selection => Number.parseInt(selection, 10))
		.filter(selection => Number.isInteger(selection) && selection > 0 && !usedPickedSelections.has(selection))
		.sort((left, right) => left - right)
		.map(selection => pickedExpiryDates?.[selection])
		.filter(Boolean);
	let remainingPickedValueIndex = 0;

	for (let selection = 1; selection <= quantity; selection += 1) {
		if (!mergedExpiryDates[selection] && remainingPickedValueIndex < remainingPickedValues.length) {
			mergedExpiryDates[selection] = remainingPickedValues[remainingPickedValueIndex];
			remainingPickedValueIndex += 1;
		}
		if (!mergedExpiryDates[selection]) {
			mergedExpiryDates[selection] = null;
		}
	}

	return mergedExpiryDates;
};

const mergePlanogramAndPickedExpiryDates = ({
	planogramExpiryDates = {},
	pickedExpiryDates = {},
	planogramItemQuantityPreService,
	quantity = 0,
	isMarket = false,
}) => {
	// Market customers can leave older stock behind even when forecast stock is zero.
	if (isMarket) {
		return { 1: resolveEarliestExpiryValue([planogramExpiryDates, pickedExpiryDates]) || null };
	}

	const resolvedQuantity = Math.max(0, Number(quantity) || 0);
	if (!resolvedQuantity) {
		return {};
	}

	if (!valueExists(planogramItemQuantityPreService)) {
		return mergeLegacyPlanogramAndPickedExpiryDates({
			planogramExpiryDates,
			pickedExpiryDates,
			quantity: resolvedQuantity,
		});
	}

	const resolvedPreServiceQuantity = Math.min(
		resolvedQuantity,
		Math.max(0, Number(planogramItemQuantityPreService) || 0),
	);

	const mergedExpiryDates = {};

	for (let selection = 1; selection <= resolvedPreServiceQuantity; selection += 1) {
		mergedExpiryDates[selection] = planogramExpiryDates?.[selection] || null;
	}

	for (
		let selection = resolvedPreServiceQuantity + 1;
		selection <= resolvedQuantity;
		selection += 1
	) {
		const pickedSelection = selection - resolvedPreServiceQuantity;
		mergedExpiryDates[selection] = pickedExpiryDates?.[pickedSelection] || null;
	}

	return mergedExpiryDates;
};

export default mergePlanogramAndPickedExpiryDates;
